import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Group,
  Text,
  Button,
  Paper,
  TextInput,
  LoadingOverlay,
  Card,
  Image,
  Stack,
  SimpleGrid,
  ThemeIcon,
  Avatar,
  Badge,
  ActionIcon,
  Modal,
  Textarea,
  Center,
  Divider,
  Grid,
  NumberInput,
} from "@mantine/core";
import {
  IconSearch,
  IconPlus,
  IconBuildingStore,
  IconStarFilled,
  IconStar,
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { ImageUploadComponent } from "../projctManagement/adminProjectManagement";
import { API_BASE_URL } from "../../config/baseUrls";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

const BASE_URL = `${API_BASE_URL}/shopfront`;

export const CreateShopModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    bannerImageUrl: "",
    logoUrl: "",
    socialLinks: { instagram: "", facebook: "", twitter: "", website: "" },
  });
  const { showSuccess, showError } = useNotification();

  const handleSubmit = async () => {
    if (!formData.name)
      return showError("Missing Data", "Shop Name is required");
    setLoading(true);

    try {
      const res = await fetch(`${BASE_URL}/shops`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });
      const data = await res.json();

      if (data.success) {
        showSuccess("Created", "Shopfront created successfully");
        onSuccess();
        onClose();
        setFormData({
          name: "",
          description: "",
          bannerImageUrl: "",
          logoUrl: "",
          socialLinks: {
            instagram: "",
            facebook: "",
            twitter: "",
            website: "",
          },
        });
      } else {
        showError("Error", data.error || "Operation failed");
      }
    } catch (e) {
      showError("Error", "Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          Create New Shopfront
        </Text>
      }
      size="lg"
      padding="xl"
    >
      <Stack gap="md">
        <LoadingOverlay visible={loading} />

        <TextInput
          label="Shop Name"
          placeholder="e.g. Anime Merch Store"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />

        <Textarea
          label="Description"
          placeholder="Brief about the shop..."
          minRows={3}
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
        />

        <Divider label="Social Media" labelPosition="center" my="xs" />

        <SimpleGrid cols={2}>
          <TextInput
            label="Instagram URL"
            placeholder="@username"
            value={formData.socialLinks.instagram}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  instagram: e.target.value,
                },
              })
            }
          />
          <TextInput
            label="Facebook URL"
            placeholder="https://facebook.com/..."
            value={formData.socialLinks.facebook}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  facebook: e.target.value,
                },
              })
            }
          />
          <TextInput
            label="Twitter / X URL"
            placeholder="https://twitter.com/..."
            value={formData.socialLinks.twitter}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  twitter: e.target.value,
                },
              })
            }
          />
          <TextInput
            label="Website URL"
            placeholder="https://myshop.com"
            value={formData.socialLinks.website}
            onChange={(e) =>
              setFormData({
                ...formData,
                socialLinks: {
                  ...formData.socialLinks,
                  website: e.target.value,
                },
              })
            }
          />
        </SimpleGrid>

        <Divider label="Branding" labelPosition="center" my="xs" />

        <Grid>
          <Grid.Col span={4}>
            <Text size="sm" fw={500} mb={4}>
              Logo (Square)
            </Text>
            <Box h={120}>
              <ImageUploadComponent
                onUpload={(url) => setFormData({ ...formData, logoUrl: url })}
                existingUrl={formData.logoUrl}
              />
            </Box>
          </Grid.Col>
          <Grid.Col span={8}>
            <Text size="sm" fw={500} mb={4}>
              Banner (Wide)
            </Text>
            <Box h={120}>
              <ImageUploadComponent
                onUpload={(url) =>
                  setFormData({ ...formData, bannerImageUrl: url })
                }
                existingUrl={formData.bannerImageUrl}
              />
            </Box>
          </Grid.Col>
        </Grid>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="blue">
            Create Shop
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default function ShopfrontManagement() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { showError } = useNotification();

  const fetchShops = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/shops`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) setShops(data.data);
    } catch (e) {
      showError("Error", "Failed to load shops");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  const handleQuickUpdate = async (id, field, value) => {
    setShops((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );

    try {
      await fetch(`${BASE_URL}/shops/${id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ [field]: value }),
      });
    } catch (error) {
      showError("Error", "Failed to update");
      fetchShops();
    }
  };

  const filteredShops = shops.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Box className="space-y-6">
      <Group justify="space-between" align="end">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            Shopfronts
          </Text>
          <Text size="sm" c="dimmed">
            Create stores and assign owners
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size={18} />}
          className="btn-ota-primary"
          onClick={() => setCreateModalOpen(true)}
        >
          Create Shop
        </Button>
      </Group>

      <Paper p="sm" radius="md" withBorder>
        <TextInput
          placeholder="Search shops..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Paper>

      <Box pos="relative" mih={300}>
        <LoadingOverlay visible={loading} />

        {filteredShops.length === 0 && !loading ? (
          <Paper p="xl" withBorder radius="md" ta="center" bg="gray.0">
            <ThemeIcon
              size={60}
              radius="xl"
              color="gray"
              variant="light"
              mb="md"
            >
              <IconBuildingStore size={30} />
            </ThemeIcon>
            <Text fw={600} size="lg">
              No Shops Found
            </Text>
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
            {filteredShops.map((shop) => (
              <Card
                key={shop.id}
                padding="0"
                radius="md"
                withBorder
                className="hover:shadow-md transition-all cursor-pointer group"
                onClick={() =>
                  navigate(
                    `/admin/dashboard/product-management/shopfronts/${shop.id}`,
                  )
                }
              >
                <Box h={100} bg="gray.2" pos="relative">
                  {shop.bannerImageUrl ? (
                    <Image src={shop.bannerImageUrl} h="100%" fit="cover" />
                  ) : (
                    <Center h="100%">
                      <IconBuildingStore size={40} color="gray" opacity={0.5} />
                    </Center>
                  )}
                  <Badge
                    color={shop.isActive ? "green" : "red"}
                    size="sm"
                    variant="filled"
                    className="absolute top-2 right-2"
                  >
                    {shop.isActive ? "Active" : "Suspended"}
                  </Badge>
                </Box>

                <Box p="md" pt={0} mt={-24} pos="relative">
                  <Group align="end">
                    <Avatar
                      src={shop.logoUrl}
                      size="lg"
                      radius="md"
                      className="border-4 border-white shadow-sm"
                      color="blue"
                    >
                      {shop.name.charAt(0)}
                    </Avatar>
                  </Group>

                  <Stack gap={2} mt="xs">
                    <Text fw={700} lineClamp={1} size="lg">
                      {shop.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Owner:{" "}
                      {shop.owner ? (
                        shop.owner.email
                      ) : (
                        <span className="text-orange-600 font-bold">
                          Unassigned
                        </span>
                      )}
                    </Text>

                    <Divider my="xs" />

                    <Group justify="space-between">
                      <Group gap={6}>
                        <ActionIcon
                          variant="transparent"
                          color={shop.isFeatured ? "yellow" : "gray"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickUpdate(
                              shop.id,
                              "isFeatured",
                              !shop.isFeatured,
                            );
                          }}
                        >
                          {shop.isFeatured ? (
                            <IconStarFilled size={18} />
                          ) : (
                            <IconStar size={18} />
                          )}
                        </ActionIcon>
                        <Text
                          size="xs"
                          c={shop.isFeatured ? "yellow.7" : "dimmed"}
                          fw={600}
                        >
                          {shop.isFeatured ? "Featured" : "Standard"}
                        </Text>
                      </Group>

                      {shop.isFeatured && (
                        <NumberInput
                          size="xs"
                          w={60}
                          min={0}
                          value={shop.priorityOrder}
                          onChange={(val) =>
                            handleQuickUpdate(shop.id, "priorityOrder", val)
                          }
                          onClick={(e) => e.stopPropagation()}
                          styles={{
                            input: { paddingRight: 5, textAlign: "center" },
                          }}
                        />
                      )}
                    </Group>
                  </Stack>
                </Box>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Box>

      <CreateShopModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={fetchShops}
      />
    </Box>
  );
}
